/**
 * LearnerLicensesSection
 *
 * Embeds inside `ProfileInfoSection` to expose:
 *   • Profession dropdown — the account's configured professions
 *     (data-driven, fetched into the lookups slice)
 *   • License list — visible only when the selected profession is
 *     flagged `isLicensed`, where licensure actually applies
 *
 * License rows are state-pickable with an "Other" escape hatch for
 * non-US jurisdictions (admins type the jurisdiction by hand). Add /
 * remove buttons mutate the local list; the parent owns the eventual
 * save via the `onProfessionChange` / `onLicensesChange` pair so this
 * component plays nicely with a single Save Changes flow.
 */

import { useMemo } from 'react';
import { Profession } from '../../apis/api-client';
import { Box, Button, IconButton, SelectField, TextField, Tooltip, Typography } from '../ui';
import { AddIcon, DeleteIcon } from '../icons';
import { SectionHeader } from '../common';
import { LicenseDraft, US_STATES, newLicenseKey } from '../../utils/licenses';

export interface LearnerLicensesSectionProps {
  professions: Profession[];
  professionId: number | null;
  licenses: LicenseDraft[];
  onProfessionChange: (professionId: number | null) => void;
  onLicensesChange: (next: LicenseDraft[]) => void;
  disabled?: boolean;
}

export default function LearnerLicensesSection({
  professions,
  professionId,
  licenses,
  onProfessionChange,
  onLicensesChange,
  disabled,
}: LearnerLicensesSectionProps) {
  const selectedProfession = useMemo(
    () => professions.find((p) => p.id === professionId) ?? null,
    [professions, professionId],
  );
  const isLicensed = selectedProfession?.isLicensed === true;

  const roleOptions = useMemo(
    () => professions.map((p) => ({ value: p.id!, label: p.name ?? '' })),
    [professions],
  );

  const updateLicense = (key: string, patch: Partial<LicenseDraft>) => {
    onLicensesChange(licenses.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  };

  const removeLicense = (key: string) => {
    onLicensesChange(licenses.filter((l) => l.key !== key));
  };

  const addLicense = () => {
    onLicensesChange([...licenses, { key: newLicenseKey(), state: '', licenseNumber: '' }]);
  };

  return (
    <Box>
      {/* Header removed — the parent SettingsCard already titles this
          block "Professional". The inner Licenses sub-section keeps
          its own header below since it represents a distinct
          mini-group that only appears for licensed professions. */}
      <Box sx={{ mb: 3, maxWidth: 360 }}>
        <SelectField
          label="Profession"
          fullWidth
          value={professionId ?? ''}
          onChange={(e) => {
            const raw = e.target.value;
            const next = raw === '' ? null : Number(raw);
            // Switching to a profession that doesn't track licensure
            // clears any licenses since they no longer apply. Admins can
            // re-add them if they flip back to a licensed profession.
            const willBeLicensed =
              professions.find((p) => p.id === next)?.isLicensed === true;
            onProfessionChange(next);
            if (isLicensed && !willBeLicensed) {
              onLicensesChange([]);
            }
          }}
          disabled={disabled}
          options={roleOptions}
          emptyLabel="Not set"
        />
        {selectedProfession && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Selected: <strong>{selectedProfession.name}</strong>
            {!isLicensed && '. Licensure isn\'t tracked for this profession.'}
          </Typography>
        )}
      </Box>

      {isLicensed && (
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, mb: 1.5 }}>
            <SectionHeader
              variant="accent"
              title="Licenses"
              subtitle="State of issue and license number for each jurisdiction this learner is credentialed in."
              sx={{ mb: 0 }}
            />
            <Button
              variant="outlined"
              size="small"
              startIcon={<AddIcon />}
              onClick={addLicense}
              disabled={disabled}
              sx={{ flexShrink: 0, textTransform: 'none' }}
            >
              Add license
            </Button>
          </Box>

          {licenses.length === 0 ? (
            <Box
              sx={{
                p: 2.5,
                borderRadius: 1.5,
                border: '1px dashed',
                borderColor: 'divider',
                bgcolor: 'grey.50',
                color: 'text.secondary',
                textAlign: 'center',
              }}
            >
              <Typography variant="body2">
                No licenses on file. Click <strong>Add license</strong> above to record one.
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {licenses.map((license, idx) => {
                const isCustom = license.state === 'OTHER';
                return (
                  <Box
                    key={license.key}
                    sx={{
                      p: 2,
                      borderRadius: 1.5,
                      border: '1px solid',
                      borderColor: 'divider',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1.5,
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}
                    >
                      <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                        License #{idx + 1}
                      </Typography>
                      <Tooltip title="Remove license">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => removeLicense(license.key)}
                          disabled={disabled}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                      <SelectField
                        label="License state"
                        value={license.state}
                        onChange={(e) => {
                          const nextState = e.target.value as string;
                          updateLicense(license.key, {
                            state: nextState,
                            // Clear customStateName when switching back
                            // to a real state code so stale text doesn't
                            // linger.
                            customStateName:
                              nextState === 'OTHER' ? license.customStateName : undefined,
                          });
                        }}
                        disabled={disabled}
                        sx={{ width: 220 }}
                        options={[
                          ...US_STATES.map((s) => ({
                            value: s.code,
                            label: `${s.name} (${s.code})`,
                          })),
                          { value: 'OTHER', label: 'Other (non-US)' },
                        ]}
                        emptyLabel="Select state"
                      />
                      {isCustom && (
                        <TextField
                          label="Jurisdiction"
                          value={license.customStateName || ''}
                          onChange={(e) =>
                            updateLicense(license.key, { customStateName: e.target.value })
                          }
                          disabled={disabled}
                          placeholder="e.g. Ontario, British Columbia"
                          sx={{ flex: '1 1 220px', minWidth: 220 }}
                        />
                      )}
                      <TextField
                        label="License number"
                        value={license.licenseNumber}
                        onChange={(e) =>
                          updateLicense(license.key, { licenseNumber: e.target.value })
                        }
                        disabled={disabled}
                        placeholder="e.g. D-58392"
                        sx={{ flex: '1 1 220px', minWidth: 220 }}
                      />
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}
